import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import CookieBanner from "@/components/CookieBanner";

import LandingPage from "@/pages/LandingPage";
import SeoHead from "@/components/SeoHead";
import MarketplacePage from "@/pages/MarketplacePage";
import ApiDetailPage from "@/pages/ApiDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import DocsPage from "@/pages/DocsPage";
import StatusPage from "@/pages/StatusPage";
import LegalPage from "@/pages/LegalPage";
import { PaymentSuccessPage, PaymentCancelPage, PaymentPendingPage } from "@/pages/PaymentResultPages";

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-40 h-4 rounded-full lp-shimmer" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
            <SeoHead />
            <Toaster richColors position="top-right" theme="dark" />
            <CookieBanner />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/products/:slug" element={<ApiDetailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/:section" element={<DocsPage />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/legal/:kind" element={<LegalPage />} />
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
              <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/aup" element={<Navigate to="/legal/aup" replace />} />
              <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
              <Route path="/cookies" element={<Navigate to="/legal/cookies" replace />} />
              <Route path="/contact" element={<Navigate to="/legal/contact" replace />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route path="/payment/pending" element={<PaymentPendingPage />} />
              <Route
                path="/dashboard/*"
                element={
                  <Protected>
                    <DashboardPage />
                  </Protected>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <Protected adminOnly>
                    <AdminPage />
                  </Protected>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}
